import './polyfills';

import { enableProdMode, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { isProdMode, versionInfo, amBaseUrl } from './config';
import { apiProviders } from './api';
import { serviceProviders } from './services';
import { HTTP_INTERCEPTORS, HttpClient } from '@angular/common/http';
import { ServerErrorInterceptor } from './services/interceptors/server-error.interceptor';
import { ServerErrorGuard } from './guards/server-error.guard';
import { Routes, withHashLocation, provideRouter } from '@angular/router';
import { PagesComponent } from './pages/pages.component';
import {
  AppGuard,
  MultipleTranslatePoHttpLoader,
  UserRightsModule,
  ShellV2Module,
  ActionMenuModule,
  ButtonModule,
  CheckboxModule,
  DateModule,
  GridModule,
  IconModule,
  IconV2Module,
  ModalModule,
  MultiselectModule,
  RadioV2Module,
  SideDrawerModule,
  SpinnerV2Module,
  TextInputModule,
  PagesModule,
  MachinesSelectorModule,
  AssetsModule,
  FileUploadV2Module,
  PagesV2Module,
  UnitsModule,
  GridV2Module,
  MultiselectV2Module,
  SelectV2Module,
  ButtonV2Module,
  CheckboxV2Module,
  TabModule,
  InputV2Module,
  ToggleV2Module,
  NotificationsV2Module,
  TileV2Module,
  ShellV2Component,
} from '@schenck/rotec-components';
import { routes } from './routes';
import { EditRedirectGuard } from './pages/edit/edit-redirect.guard';
import { ConfigService } from './services/config.service';
import { TranslateLoader, provideTranslateService } from '@ngx-translate/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { FormsModule } from '@angular/forms';
import { PortalModule } from '@angular/cdk/portal';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { LayoutModule } from '@angular/cdk/layout';
import { MatRadioModule } from '@angular/material/radio';
import { environment } from './environment/environment';
import { EditModule } from './pages/edit/edit.module';
import { CommonModule, registerLocaleData } from '@angular/common';
import { PipesModule } from './pipes/pipes.module';
import { bootstrapApplication } from '@angular/platform-browser';

import deLocale from '@angular/common/locales/de';
import enLocale from '@angular/common/locales/en';
import esLocale from '@angular/common/locales/es';
import frLocale from '@angular/common/locales/fr';
import itLocale from '@angular/common/locales/it';
import nlLocale from '@angular/common/locales/nl';
import svLocale from '@angular/common/locales/sv';
import plLocale from '@angular/common/locales/pl';
import trLocale from '@angular/common/locales/tr';
import zhLocale from '@angular/common/locales/zh';
import huLocale from '@angular/common/locales/hu';
import arLocale from '@angular/common/locales/ar';
import csLocale from '@angular/common/locales/cs';
import koLocale from '@angular/common/locales/ko';
import jaLocale from '@angular/common/locales/ja';
import ptLocale from '@angular/common/locales/pt';

registerLocaleData(deLocale, 'de');
registerLocaleData(enLocale, 'en');
registerLocaleData(esLocale, 'es');
registerLocaleData(frLocale, 'fr');
registerLocaleData(itLocale, 'it');
registerLocaleData(nlLocale, 'nl');
registerLocaleData(svLocale, 'sv');
registerLocaleData(plLocale, 'pl');
registerLocaleData(trLocale, 'tr');
registerLocaleData(zhLocale, 'zh');
registerLocaleData(huLocale, 'hu');
registerLocaleData(csLocale, 'cs');
registerLocaleData(arLocale, 'ar');
registerLocaleData(koLocale, 'ko');
registerLocaleData(jaLocale, 'ja');
registerLocaleData(ptLocale, 'pt');

function HttpPoLoaderFactory(http: HttpClient) {
  return new MultipleTranslatePoHttpLoader(http, [
    { prefix: 'assets/rotec-locales/', suffix: '.po' },
    { prefix: 'assets/locales/', suffix: '.po' },
  ]);
}

const appRoutes: Routes = [
  {
    path: '',
    component: PagesComponent,
    canActivate: [AppGuard],
    children: [
      {
        path: routes.search.url,
        loadComponent: () =>
          import('./pages/search/search.component').then((m) => m.SearchComponent),
      },
      {
        path: routes.compare.url,
        loadComponent: () =>
          import('./pages/compare/compare.component').then((m) => m.CompareComponent),
      },
      {
        path: routes.edit.url,
        loadComponent: () => import('./pages/edit/edit.component').then((m) => m.EditComponent),
        children: [
          {
            path: '',
            redirectTo: 'templates',
            pathMatch: 'full',
          },
          {
            path: 'templates',
            loadComponent: () =>
              import('./pages/edit/landing-page/landing-page.component').then(
                (m) => m.LandingPageComponent
              ),
            canActivate: [EditRedirectGuard],
          },
          {
            path: 'template/:id',
            loadComponent: () =>
              import('./pages/edit/template-editor/template-editor.component').then(
                (m) => m.TemplateEditorComponent
              ),
          },
        ],
        canActivate: [ConfigService],
      },
      {
        path: routes.unhandledError.url,
        loadComponent: () =>
          import('./components/service-unavailable/service-unavailable.component').then(
            (m) => m.ServiceUnavailableComponent
          ),
        canActivate: [ServerErrorGuard],
      },
      {
        path: '',
        redirectTo: routes.search.url,
        pathMatch: 'full',
      },
    ],
  },
  { path: '**', redirectTo: routes.search.url },
];

if (isProdMode) {
  enableProdMode();
}

bootstrapApplication(ShellV2Component, {
  providers: [
    provideZoneChangeDetection(),
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpPoLoaderFactory,
        deps: [HttpClient],
      },
    }),
    importProvidersFrom(
      FormsModule,
      PortalModule,
      DragDropModule,
      LayoutModule,
      MatRadioModule,
      // Rotec-Components Library
      UserRightsModule.forRoot(environment.umBaseUrl),
      ShellV2Module.forRoot({
        isProduction: isProdMode,
        appLogoAndName: {
          name: 'Report Center',
          logoUrl: './assets/img/report-center.png',
        },
        versionInfo,
        msalConfig: environment.msalConfig,
        azureEndpoint: environment.azureEndpoint,
        baseUrl: environment.apiBaseUrl,
        usBaseUrl: environment.usBaseUrl,
      }),
      ActionMenuModule,
      ButtonModule,
      CheckboxModule,
      DateModule,
      GridModule,
      IconModule,
      IconV2Module,
      ModalModule.forRoot(),
      MultiselectModule,
      RadioV2Module,
      SideDrawerModule.forRoot(),
      SpinnerV2Module.forRoot(),
      TextInputModule,
      PagesModule,
      MachinesSelectorModule,
      AssetsModule.forRoot(amBaseUrl),
      FileUploadV2Module.forRoot({
        uploadBaseUrl: amBaseUrl,
        uploadApi: 'files',
        fallbackImgSrcForFileUpload: './assets/rotor-data-center.png',
        fallbackImgSrcForPipe: './assets/img/schenckone-hoch.png',
      }),
      EditModule,
      PagesV2Module,
      UnitsModule.forRoot(),
      GridV2Module,
      MultiselectV2Module,
      SelectV2Module,
      ButtonV2Module,
      RadioV2Module,
      CheckboxV2Module,
      TabModule,
      InputV2Module,
      ToggleV2Module,
      CommonModule,
      PipesModule,
      NotificationsV2Module,
      TileV2Module
    ),
    ...apiProviders,
    ...serviceProviders,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ServerErrorInterceptor,
      multi: true,
    },
    ServerErrorGuard,
    provideAnimations(),
    provideRouter(appRoutes, withHashLocation()),
  ],
}).catch((err) => console.log(err));
